import { isNil } from '#packages/data-collection/libs/lodash';

import { createModuleLogger } from '#apps/client-web/app/services/logger';
import { LocalForageDbService } from '#apps/client-web/app/services/local-forage-db';
import { LocalForageKeyValueRepo } from '#apps/client-web/app/repositories/local-forage-kv-repo';

import { type AuditId } from '#packages/data-collection/models/audit';
import {
  type AuditForm,
  type AuditFormType,
  AuditFormSchema,
  AuditFormNotFoundError,
} from '#packages/data-collection/models/audit-form';
import { type KeyValueRepo } from '#packages/data-collection/repositories/kv-repo';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';

type AuditFormEntity = {
  [key in AuditFormType]?: ReturnType<typeof AuditFormSchema.encode>;
};

export type AuditFormServiceOptions = {
  /**
   * Repository for storing audit forms
   */
  repo: KeyValueRepo<AuditId, AuditFormEntity>;
} & LoggingContext;

export class AuditFormService {
  protected readonly opts: AuditFormServiceOptions;

  constructor(opts: AuditFormServiceOptions) {
    this.opts = opts;
  }

  protected async loadAuditFormsByAuditId({
    auditId,
  }: {
    auditId: AuditId;
  }): Promise<AuditFormEntity> {
    const auditForms = await this.opts.repo.get(auditId);
    return auditForms ?? {};
  }

  async loadByType({
    auditId,
    formType,
  }: {
    auditId: AuditId;
    formType: AuditFormType;
  }): Promise<AuditForm | null> {
    const { logger } = this.opts;

    logger?.debug({ auditId, formType }, 'Loading audit form');

    const auditForms = await this.loadAuditFormsByAuditId({ auditId });
    const auditForm = auditForms[formType] ?? null;
    const found = !isNil(auditForm);

    logger?.debug({ auditId, formType, found, auditForm }, 'Loaded audit form');

    return isNil(auditForm) ? null : AuditFormSchema.decode(auditForm);
  }

  async save({
    auditId,
    form,
  }: {
    auditId: AuditId;
    form: AuditForm;
  }): Promise<AuditForm> {
    const { logger } = this.opts;

    logger?.debug({ auditId, form }, 'Saving audit form');

    const auditForms = await this.loadAuditFormsByAuditId({ auditId });
    auditForms[form.type] = AuditFormSchema.encode(form);

    await this.opts.repo.set(auditId, auditForms);

    logger?.debug({ auditId, form }, 'Saved audit form');

    return form;
  }

  async deleteByType({
    auditId,
    formType,
  }: {
    auditId: AuditId;
    formType: AuditFormType;
  }): Promise<AuditForm> {
    const { logger } = this.opts;

    logger?.debug({ auditId, formType }, 'Deleting audit form');

    // Load the existing audit form
    const auditForms = await this.loadAuditFormsByAuditId({ auditId });
    const auditForm = auditForms[formType];

    if (isNil(auditForm)) {
      throw new AuditFormNotFoundError(auditId, formType);
    }

    // Remove the form from the audit forms
    delete auditForms[formType];
    await this.opts.repo.set(auditId, auditForms);

    logger?.debug({ auditId, formType, auditForm }, 'Deleted audit form');

    return AuditFormSchema.decode(auditForm);
  }
}

const logger = createModuleLogger('audit-form-service');

export const auditFormDatabase =
  LocalForageDbService.createInstance('audit-forms');

export const auditFormRepo = new LocalForageKeyValueRepo<AuditFormEntity>({
  db: auditFormDatabase,
  logger,
});

export const auditFormService = new AuditFormService({
  repo: auditFormRepo,
  logger,
});
